import React, { useState, useMemo, type SetStateAction } from "react";
import {
    Alert,
    CardContent,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Typography,
} from "@mui/material";
import { useTranslation } from "shared/hooks/useTranslation";
import { ExpandableCard } from "shared/ui/ComposedCards";
import MoneyInput from "shared/ui/MoneyInput";
import { formatCurrencyAmount, languageToLocale } from "shared/money/balances";
import type { LiquidityPool } from "features/wallet/api/Wallet";
import FormDialog from "shared/ui/FormDialog";
import FormActions from "shared/ui/FormActions";
import { FORM_FIELD_GAP } from "shared/ui/formConstants";

export interface TransferCardProps {
    pools: LiquidityPool[];
    onSubmit: (data: { sourcePoolId: string; targetPoolId: string; amount: number }) => Promise<{ success: boolean; error?: string }>;
    open?: boolean;
    onClose?: () => void;
    variant?: "inline" | "dialog";
    mutationsDisabled?: boolean;
}

const TransferCard: React.FC<TransferCardProps> = ({ pools, onSubmit, open, onClose, variant = "inline", mutationsDisabled = false }) => {
    const { t, i18n } = useTranslation();
    const [internalIsOpen, setInternalIsOpen] = useState(false);
    const isOpen = open ?? internalIsOpen;
    const setIsOpen = onClose
        ? (next: SetStateAction<boolean>) => {
            const resolved = typeof next === "function" ? next(isOpen) : next;
            if (!resolved) onClose();
        }
        : setInternalIsOpen;
    const [sourcePoolId, setSourcePoolId] = useState("");
    const [targetPoolId, setTargetPoolId] = useState("");
    const [amount, setAmount] = useState("");
    const [error, setError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);

    const sameCurrencyPools = useMemo(() => {
        if (!sourcePoolId) return pools;
        const source = pools.find((p) => p._id === sourcePoolId);
        if (!source) return pools;
        return pools.filter((p) => p.currency === source.currency);
    }, [pools, sourcePoolId]);

    const transferMaxHint = useMemo(() => {
        if (!sourcePoolId || pools.length < 2) return null;
        const source = pools.find((p) => p._id === sourcePoolId);
        if (!source) return null;
        const max = Math.max(0, source.currentBalance);
        const formatted = formatCurrencyAmount(max, source.currency, languageToLocale(i18n.language));
        return t("wallet.transferMaxAvailable", { amount: formatted });
    }, [sourcePoolId, pools, t, i18n.language]);
    const sourcePool = pools.find((pool) => pool._id === sourcePoolId);
    const targetPool = pools.find((pool) => pool._id === targetPoolId);
    const transferAmount = Number(amount);
    const insufficientBalance = Boolean(
        sourcePool && Number.isFinite(transferAmount) && transferAmount > sourcePool.currentBalance,
    );
    const hasCompatibleTarget = sameCurrencyPools.some((pool) => pool._id !== sourcePoolId);
    const locale = languageToLocale(i18n.language);

    const handleReset = () => {
        setSourcePoolId("");
        setTargetPoolId("");
        setAmount("");
        setError(null);
    };

    const handleCancel = () => {
        if (submitting) return;
        handleReset();
        setIsOpen(false);
    };

    const handleSourceChange = (id: string) => {
        setSourcePoolId(id);
        setTargetPoolId("");
    };

    const handleSubmit = async () => {
        if (submitting) return;
        if (!sourcePoolId || !targetPoolId) {
            setError(t("wallet.selectBothPools"));
            return;
        }
        if (sourcePoolId === targetPoolId) {
            setError(t("wallet.samePoolError"));
            return;
        }
        const amt = Number(amount);
        if (isNaN(amt) || amt <= 0) {
            setError(t("wallet.invalidAmount"));
            return;
        }
        if (insufficientBalance) {
            setError(t("wallet.insufficientTransferPreview"));
            return;
        }
        setError(null);
        setSubmitting(true);
        try {
            const result = await onSubmit({
                sourcePoolId,
                targetPoolId,
                amount: amt,
            });
            if (result.success) {
                handleReset();
                setIsOpen(false);
            } else {
                setError(result.error ?? t("common.error"));
            }
        } finally {
            setSubmitting(false);
        }
    };

    const canSubmit = pools.length >= 2
        && Boolean(sourcePoolId && targetPoolId && amount)
        && Number.isFinite(transferAmount)
        && transferAmount > 0;

    const inner = (
        <Stack spacing={FORM_FIELD_GAP}>
            <Alert severity="info">
                {t("wallet.transferRecordHint")}
            </Alert>
            {pools.length < 2 ? (
                <Typography variant="body2" color="text.secondary">
                    {t("wallet.transferRequiresTwoPools")}
                </Typography>
            ) : null}
            {error ? <Alert severity="error">{error}</Alert> : null}
            <FormControl fullWidth size="small" disabled={pools.length < 2}>
                <InputLabel>{t("wallet.sourceAccount")}</InputLabel>
                <Select
                    value={sourcePoolId}
                    label={t("wallet.sourceAccount")}
                    onChange={(e) => handleSourceChange(e.target.value)}
                >
                    {pools.map((p) => (
                        <MenuItem key={p._id} value={p._id}>
                            {p.name} ({p.currency})
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
            <FormControl fullWidth size="small">
                <InputLabel>{t("wallet.destinationAccount")}</InputLabel>
                <Select
                    value={targetPoolId}
                    label={t("wallet.destinationAccount")}
                    onChange={(e) => setTargetPoolId(e.target.value)}
                    disabled={!sourcePoolId || pools.length < 2}
                >
                    {sameCurrencyPools
                        .filter((p) => p._id !== sourcePoolId)
                        .map((p) => (
                            <MenuItem key={p._id} value={p._id}>
                                {p.name} ({p.currency})
                            </MenuItem>
                        ))}
                </Select>
            </FormControl>
            {sourcePool && !hasCompatibleTarget ? (
                <Typography variant="body2" color="text.secondary">
                    {t("wallet.transferRequiresCompatibleAccount", { currency: sourcePool.currency })}
                </Typography>
            ) : null}
            <MoneyInput
                label={t("forms.amount")}
                value={amount}
                onValueChange={setAmount}
                fullWidth
                size="small"
                disabled={!sourcePoolId || pools.length < 2}
            />
            {transferMaxHint ? (
                <Typography variant="body2" color="text.secondary">
                    {transferMaxHint}
                </Typography>
            ) : null}
            {sourcePool && targetPool && Number.isFinite(transferAmount) && transferAmount > 0 ? (
                <Stack spacing={0.5} sx={{ p: 1.5, borderRadius: 2, bgcolor: "action.hover" }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{t("wallet.resultingBalances")}</Typography>
                    <Typography variant="body2" color="text.secondary">
                        {t("wallet.balanceAfterMovement", {
                            name: sourcePool.name,
                            before: formatCurrencyAmount(sourcePool.currentBalance, sourcePool.currency, locale),
                            after: formatCurrencyAmount(sourcePool.currentBalance - transferAmount, sourcePool.currency, locale),
                        })}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {t("wallet.balanceAfterMovement", {
                            name: targetPool.name,
                            before: formatCurrencyAmount(targetPool.currentBalance, targetPool.currency, locale),
                            after: formatCurrencyAmount(targetPool.currentBalance + transferAmount, targetPool.currency, locale),
                        })}
                    </Typography>
                    {insufficientBalance && <Alert severity="error">{t("wallet.insufficientTransferPreview")}</Alert>}
                </Stack>
            ) : null}
            <FormActions
                submitLabel={t("wallet.saveRecord")}
                submitButtonType="button"
                onSubmit={handleSubmit}
                onCancel={handleCancel}
                submitDisabled={!canSubmit || mutationsDisabled || insufficientBalance}
                submitLoading={submitting}
            />
        </Stack>
    );

    return (
        variant === "dialog" ? (
            <FormDialog
                open={isOpen}
                onClose={() => {
                    if (!submitting) handleCancel();
                }}
                title={t("wallet.transfer")}
                disabled={submitting}
            >
                {inner}
            </FormDialog>
        ) : (
            <ExpandableCard title={t("wallet.transfer")} open={isOpen} onOpenChange={setIsOpen}>{<CardContent>{inner}</CardContent>}</ExpandableCard>
        )
    );
};

export default TransferCard;
